import React, { useState } from "react";
import type { DragEndEvent } from "@dnd-kit/core";
import { useToast } from "@chakra-ui/react";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import type { Company } from "newui/features/Company/types";
import { submissionComplianceSteps } from "guides/features/Settings";
import type { ComplianceQuestion } from "newui/features/Settings/types/ComplianceQuestion.type";
import { ReadOnlyGuard, useIsReadOnlyMode } from "newui/features/ExecutiveView";
import {
	useComplianceQuestions,
	useCreateComplianceQuestion,
	useUpdateComplianceQuestion,
	useDeleteComplianceQuestion,
	useReorderComplianceQuestions,
	arrayMove,
} from "./useComplianceQuestions";
import {
	ComplianceQuestionsLoading,
	ComplianceQuestionsError,
	ComplianceQuestionsEmpty,
	ComplianceQuestionsList,
	ComplianceQuestionsAddBar,
	ComplianceDeleteModal,
} from "./components";

export interface SubmissionComplianceTabProps {
	company: Company;
}

/**
 * Non-persisted sample row shown only while the "Show Me" guide runs and there
 * are no real questions yet, so the tour's row-level steps have a target.
 * Negative id guarantees it never collides with a persisted question.
 */
const DEMO_QUESTION: ComplianceQuestion = {
	id: -1,
	question: "Valid insurance certificate provided?",
	sort_order: 0,
};

export const SubmissionComplianceTab = ({
	company,
}: SubmissionComplianceTabProps) => {
	const toast = useToast();
	const companyId = company?.id;

	const { data: questions = [], isLoading, isError, refetch } =
		useComplianceQuestions(companyId);

	const createMutation = useCreateComplianceQuestion(companyId);
	const updateMutation = useUpdateComplianceQuestion(companyId);
	const deleteMutation = useDeleteComplianceQuestion(companyId);
	const reorderMutation = useReorderComplianceQuestions(companyId);

	const isReadOnly = useIsReadOnlyMode();

	const [newQuestion, setNewQuestion] = useState("");

	// The "Show Me" guide points at row-level controls (drag handle, edit,
	// delete). When there are no real questions yet, inject a temporary,
	// non-persisted demo row for the duration of the tour so those steps have a
	// target. Toggled via the InstructionsInfoBox onBeforeShowMe/onAfterShowMe.
	const [demoActive, setDemoActive] = useState(false);

	// Question pending deletion — drives the confirm modal (req: confirm before
	// delete). null = no modal open.
	const [pendingDelete, setPendingDelete] = useState<ComplianceQuestion | null>(
		null
	);

	const showError = (description: string) => {
		toast({
			title: "Error",
			description,
			status: "error",
			duration: 4000,
			isClosable: true,
		});
	};

	const handleAdd = () => {
		if (isReadOnly) {
			return;
		}
		const trimmed = newQuestion.trim();
		if (trimmed.length === 0) {
			return;
		}
		createMutation.mutate(trimmed, {
			onSuccess: () => setNewQuestion(""),
			onError: () => showError("Failed to add the question. Please try again."),
		});
	};

	const handleEdit = (id: number, question: string) => {
		if (isReadOnly) {
			return;
		}
		if (id < 0) {
			// Demo row used by the guide — never persisted.
			return;
		}
		updateMutation.mutate(
			{ id, question },
			{
				onError: () =>
					showError("Failed to update the question. Please try again."),
			}
		);
	};

	// Open the confirm modal instead of deleting immediately. The demo row used
	// by the guide has a negative id and is never persisted, so ignore it.
	const requestDelete = (id: number) => {
		if (isReadOnly) {
			return;
		}
		if (id < 0) {
			return;
		}
		const target = questions.find((q) => q.id === id);
		if (target) {
			setPendingDelete(target);
		}
	};

	const confirmDelete = () => {
		if (!pendingDelete) {
			return;
		}
		const { id } = pendingDelete;
		deleteMutation.mutate(id, {
			onSuccess: () => setPendingDelete(null),
			onError: () => {
				setPendingDelete(null);
				showError("Failed to delete the question. Please try again.");
			},
		});
	};

	const handleDragEnd = (event: DragEndEvent) => {
		if (isReadOnly) {
			return;
		}
		const { active, over } = event;
		if (!over || active.id === over.id) {
			return;
		}
		const oldIndex = questions.findIndex((q) => q.id === active.id);
		const newIndex = questions.findIndex((q) => q.id === over.id);
		if (oldIndex === -1 || newIndex === -1) {
			return;
		}
		const reordered = arrayMove(questions, oldIndex, newIndex);
		reorderMutation.mutate(
			reordered.map((q) => q.id),
			{
				onError: () =>
					showError("Failed to reorder questions. The order was reverted."),
			}
		);
	};

	const handleNewKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAdd();
		}
	};

	// While the guide runs against an empty list, show the demo row so the
	// reorder/edit/delete steps have a target. Real questions take precedence.
	const displayQuestions =
		demoActive && questions.length === 0 ? [DEMO_QUESTION] : questions;

	const savingId =
		updateMutation.isPending && updateMutation.variables != null
			? updateMutation.variables.id
			: null;

	const renderBody = () => {
		if (isLoading) {
			return <ComplianceQuestionsLoading />;
		}
		if (isError) {
			return <ComplianceQuestionsError onRetry={refetch} />;
		}
		if (displayQuestions.length === 0) {
			return <ComplianceQuestionsEmpty />;
		}
		return (
			<ComplianceQuestionsList
				questions={displayQuestions}
				savingId={savingId}
				onEdit={handleEdit}
				onDelete={requestDelete}
				onDragEnd={handleDragEnd}
			/>
		);
	};

	return (
		<div data-testid="submission-compliance-tab">
			<InstructionsInfoBox
				data-testid="compliance-questions-info"
				title="Submission compliance questions"
				defaultExpanded
				guideSteps={submissionComplianceSteps}
				showGuideLabel="Show me how"
				onBeforeShowMe={() => {
					// Give the tour a row to point at if the list is empty.
					if (questions.length === 0) {
						setDemoActive(true);
					}
				}}
				onAfterShowMe={() => setDemoActive(false)}
			>
				Manage the compliance questions for{" "}
				{company?.name ?? "your company"}. These are what the RFx Lead checks
				each supplier submission against to decide which proceed to the
				Evaluation stage. Drag the handle to reorder questions, or use{" "}
				<strong>Show me how</strong> for a quick walkthrough.
			</InstructionsInfoBox>

			<div className="mt-6">
				<div className="border-b border-gray-200 px-3 py-2">
					<span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
						Compliance Question
					</span>
				</div>

				<div className="mt-2">{renderBody()}</div>

				<ReadOnlyGuard>
					<ComplianceQuestionsAddBar
						value={newQuestion}
						isLoading={createMutation.isPending}
						onChange={(e) => setNewQuestion(e.target.value)}
						onKeyDown={handleNewKeyDown}
						onAdd={handleAdd}
					/>
				</ReadOnlyGuard>
			</div>

			<ComplianceDeleteModal
				pendingDelete={pendingDelete}
				isDeleting={deleteMutation.isPending}
				onConfirm={confirmDelete}
				onCancel={() => setPendingDelete(null)}
			/>
		</div>
	);
};
